import React, { useEffect, useState } from "react";
import axios from "axios";
import { NewsCard } from "../Dashboard/NewsFeed";
import AdminTabs from "../Admin/AdminTabs";

const API = () => process.env.REACT_APP_API_ROOT;

// <input type="datetime-local"> wants local time without a zone: "2026-11-02T09:30".
const toLocalInput = (date) => {
  const d = new Date(date);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const blank = () => ({ title: "", description: "", url: "", imageUrl: "", publishedAt: toLocalInput(new Date()) });

const inputClass =
  "mt-1 block w-full rounded-lg border border-gray-300 bg-gray-50 p-2.5 text-sm text-gray-900 focus:border-mobstr-purple focus:ring-mobstr-purple";

function Field({ label, error, hint, children }) {
  return (
    <label className="block">
      <span className="text-sm font-medium text-gray-900">{label}</span>
      {children}
      {hint && !error && <span className="mt-1 block text-xs text-gray-500">{hint}</span>}
      {error && <span className="mt-1 block text-xs text-red-600">{error}</span>}
    </label>
  );
}

/**
 * Super users: add, edit and delete the news shown on every customer's
 * dashboard (the three newest items whose date has arrived).
 */
export default function NewsAdmin({ userLoggedIn }) {
  const [items, setItems] = useState(null);
  const [form, setForm] = useState(blank());
  const [editingId, setEditingId] = useState(null);
  const [errors, setErrors] = useState({});
  const [message, setMessage] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () =>
    axios
      .get(`${API()}/admin/news`)
      .then((r) => setItems(r.data.items))
      .catch(() => setItems([]));

  useEffect(() => {
    if (userLoggedIn?.isSuperUser) load();
  }, [userLoggedIn?.isSuperUser]);

  if (!userLoggedIn) return null;
  if (!userLoggedIn.isSuperUser) {
    return <p className="text-gray-600">This page is only available to Mobstr super users.</p>;
  }

  const set = (key) => (e) => {
    setForm((f) => ({ ...f, [key]: e.target.value }));
    if (errors[key]) setErrors((x) => ({ ...x, [key]: undefined }));
  };

  const reset = () => {
    setForm(blank());
    setEditingId(null);
    setErrors({});
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage(null);
    const body = { ...form, publishedAt: form.publishedAt ? new Date(form.publishedAt).toISOString() : undefined };
    try {
      if (editingId) await axios.put(`${API()}/admin/news/${editingId}`, body);
      else await axios.post(`${API()}/admin/news`, body);
      setMessage({ ok: true, text: editingId ? "News item updated." : "News item added." });
      reset();
      load();
    } catch (err) {
      const data = err?.response?.data?.error;
      setErrors(data?.fields || {});
      setMessage({ ok: false, text: data?.message || "Couldn't save the news item. Please try again." });
    } finally {
      setSaving(false);
    }
  };

  const edit = (item) => {
    setEditingId(item.id);
    setForm({ title: item.title, description: item.description, url: item.url, imageUrl: item.imageUrl || "", publishedAt: toLocalInput(item.publishedAt) });
    setErrors({});
    setMessage(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const remove = async (item) => {
    if (!window.confirm(`Delete "${item.title}"? It will disappear from every dashboard.`)) return;
    try {
      await axios.delete(`${API()}/admin/news/${item.id}`);
      if (editingId === item.id) reset();
      setMessage({ ok: true, text: "News item deleted." });
      load();
    } catch {
      setMessage({ ok: false, text: "Couldn't delete the news item. Please try again." });
    }
  };

  const now = Date.now();
  const live = (items || []).filter((i) => new Date(i.publishedAt).getTime() <= now).slice(0, 3).map((i) => i.id);
  const preview = {
    title: form.title || "Headline",
    description: form.description || "A short summary of the story.",
    url: form.url || "#",
    imageUrl: form.imageUrl,
    publishedAt: form.publishedAt ? new Date(form.publishedAt) : new Date(),
  };

  return (
    <div className="flex flex-col gap-8">
      <div>
        <AdminTabs userLoggedIn={userLoggedIn} />
        <h1 className="text-xl font-medium">Manage news</h1>
        <p className="mt-1 max-w-3xl text-gray-600">
          Every customer's dashboard shows the three newest items whose date has arrived. Give an item a future date to schedule it.
        </p>
      </div>

      <div className="grid gap-8 lg:grid-cols-2">
        <form onSubmit={save} noValidate className="flex flex-col gap-4 rounded-lg border border-gray-200 bg-white p-6 shadow">
          <h2 className="text-lg font-medium">{editingId ? "Edit news item" : "Add a news item"}</h2>
          <Field label="Headline" error={errors.title}>
            <input className={inputClass} value={form.title} onChange={set("title")} maxLength={200} aria-label="Headline" />
          </Field>
          <Field label="Summary" error={errors.description}>
            <textarea className={inputClass} rows={4} value={form.description} onChange={set("description")} maxLength={2000} aria-label="Summary" />
          </Field>
          <Field label="Link to the article" error={errors.url} hint="Opens in a new tab.">
            <input className={inputClass} value={form.url} onChange={set("url")} placeholder="https://" aria-label="Link to the article" />
          </Field>
          <Field label="Image link (optional)" error={errors.imageUrl}>
            <input className={inputClass} value={form.imageUrl} onChange={set("imageUrl")} placeholder="https://" aria-label="Image link" />
          </Field>
          <Field label="Publish date" error={errors.publishedAt} hint="In your local time. A future date schedules the item.">
            <input type="datetime-local" className={inputClass} value={form.publishedAt} onChange={set("publishedAt")} aria-label="Publish date" />
          </Field>
          <div className="flex items-center gap-3">
            <button
              type="submit"
              disabled={saving}
              className="w-fit rounded bg-[#2c4251] px-5 py-2.5 text-center text-sm font-medium uppercase text-white hover:bg-[#3d627a] disabled:opacity-60"
            >
              {saving ? "Saving…" : editingId ? "Save changes" : "Add news item"}
            </button>
            {editingId && (
              <button type="button" onClick={reset} className="text-sm text-gray-600 underline">
                Cancel editing
              </button>
            )}
          </div>
          {message && (
            <p role="status" className={`text-sm ${message.ok ? "text-green-700" : "text-red-600"}`}>
              {message.text}
            </p>
          )}
        </form>

        <div>
          <h2 className="mb-3 text-sm font-medium uppercase tracking-wide text-gray-500">Preview</h2>
          <NewsCard item={preview} />
        </div>
      </div>

      <div>
        <h2 className="mb-3 text-lg font-medium">All news</h2>
        {items === null ? (
          <p className="text-gray-500">Loading…</p>
        ) : items.length === 0 ? (
          <p className="text-gray-500">No news yet.</p>
        ) : (
          <ul className="divide-y divide-gray-200 rounded-lg border border-gray-200 bg-white shadow">
            {items.map((item) => {
              const scheduled = new Date(item.publishedAt).getTime() > now;
              return (
                <li key={item.id} className="flex flex-wrap items-center gap-4 p-4">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="truncate font-medium text-gray-900">{item.title}</span>
                      {live.includes(item.id) && <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800">On dashboards</span>}
                      {scheduled && <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">Scheduled</span>}
                    </div>
                    <div className="text-xs text-gray-500">
                      {new Date(item.publishedAt).toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                    </div>
                  </div>
                  <button type="button" onClick={() => edit(item)} className="text-sm font-medium text-mobstr-purple">
                    Edit
                  </button>
                  <button type="button" onClick={() => remove(item)} className="text-sm font-medium text-red-600">
                    Delete
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
